import { type CareerProfileIguanaObject } from 'CareerProfiles';
import { ProgramPreferencesStateKey } from './Careers.types';

// Levels at which a completed degree experience satisfies the minimum-education requirement
// for submission.
const ALLOW_SUBMIT_DEGREE_LEVELS = [
    'bachelors_undergraduate',
    'masters_graduate',
    'doctorate',
    'international_credentials',
    'professional_certifications',
    'other',
];

type EducationExperienceForSubmitGate = {
    degree_program?: boolean;
    has_graduated?: boolean | null;
    will_not_complete?: boolean | null;
    graduation_year?: number | null;
    degree_level?: string | null;
};

// An education experience is "completed" if the applicant has affirmatively graduated
// (new form flow) OR the record comes from the legacy flow where the completion signal was
// `will_not_complete: false` with a non-future graduation year. Accepting both keeps the
// submit gate compatible with pre-PR-3 data that never set `has_graduated` (model validation
// still permits that state; see
// `CareerProfile::EducationExperience#validate_has_or_will_graduate_if_degree_program`)
// and with admin-edited records (the admin form does not require `has_graduated`).
//
// Note: this function checks experience completion only — it does NOT verify the experience
// is a degree program. Callers must apply `degree_program === true` separately.
function isCompletedEducationExperience(exp: EducationExperienceForSubmitGate): boolean {
    // An explicit "No" on the new-flow question overrides any legacy signals.
    // `resetGraduateState` on save keeps `will_not_complete` consistent with the new flags,
    // so a legacy fallback here would only ever re-interpret data the user just set to "No."
    if (exp.has_graduated === true) return true;
    if (exp.has_graduated === false) return false;
    // Legacy fallback: the applicant never answered the new `has_graduated` question
    // (old form, or admin save that skipped it). Trust the legacy pair if present.
    return (
        exp.will_not_complete === false &&
        typeof exp.graduation_year === 'number' &&
        exp.graduation_year <= new Date().getFullYear()
    );
}

// FIXME: this is not multi-program compatible and will return business_admin_* states for
// programs NOT in the master_of_business_administration program family.
// At the moment, that does not matter because we only display things from these configs on
// the Submit Application form if:
//  1. The config says that saves should be disabled, in which case the wording is generic enough
//     to work for any program, or
//  2. The program family of the Cohort being applied to is master_of_business_administration, because we
//     generally consider applications to that program family for multiple programs.
// If we ever want to display this stuff outside of those two contexts above, we need to rethink this.
export function determineProgramPreferencesStateKey(
    careerProfile: CareerProfileIguanaObject,
): ProgramPreferencesStateKey {
    const isLowerEducation = ['associates', 'high_school'].includes(
        careerProfile.survey_highest_level_completed_education_description as string,
    );

    const educationExperiences = (
        careerProfile as unknown as { education_experiences?: EducationExperienceForSubmitGate[] }
    ).education_experiences;
    const hasEligibleCompletedDegree = educationExperiences?.some(
        exp =>
            exp.degree_program === true &&
            isCompletedEducationExperience(exp) &&
            ALLOW_SUBMIT_DEGREE_LEVELS.includes(exp.degree_level ?? ''),
    );

    if (isLowerEducation || !hasEligibleCompletedDegree) {
        return ProgramPreferencesStateKey.generic_minimum_education_not_met;
    }

    if (careerProfile.survey_years_full_time_experience === '0_1') {
        return ProgramPreferencesStateKey.business_admin_minimum_experience;
    }

    if (careerProfile.survey_years_full_time_experience === '2_6' && careerProfile.age && careerProfile.age < 29) {
        return ProgramPreferencesStateKey.business_admin_younger_with_low_experience;
    }

    if (careerProfile.survey_years_full_time_experience === '2_6' && careerProfile.age && careerProfile.age >= 29) {
        return ProgramPreferencesStateKey.business_admin_older_with_low_experience;
    }

    // Age is not a factor for this state
    if (careerProfile.survey_years_full_time_experience === '7_10') {
        return ProgramPreferencesStateKey.business_admin_middle_experience;
    }

    if (
        ['11_15', '16_plus'].includes(careerProfile.survey_years_full_time_experience as string) &&
        careerProfile.age &&
        careerProfile.age < 31
    ) {
        return ProgramPreferencesStateKey.business_admin_younger_with_high_experience;
    }

    // All other scenarios have been handled above, so this is the only possible outcome left.
    // In this state, `survey_years_full_time_experience` is either '11_15' or '16_plus' and
    // the user's age must be greater than or equal to 31.
    return ProgramPreferencesStateKey.business_admin_older_with_high_experience;
}

export default determineProgramPreferencesStateKey;
